import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Search, X, ChevronDown, Check } from 'lucide-react';
import { ralColors, colorCategories, getColorById } from '../data/ralColors';
import { useTranslation } from '../context/TranslationContext';

/**
 * Color picker used across configurators.
 *
 * - default (popover): absolute dropdown (options panels: interior/exterior)
 * - inline: same open/close UX, but expands in-flow for Dialogs (side panels)
 *   so clicks work and the list collapses after a color is chosen.
 */
const ColorPicker = ({
  selectedColor,
  onColorChange,
  showSearch = true,
  inline = false,
  maxListHeight = 220,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const rootRef = useRef(null);

  const allColors = useMemo(
    () =>
      ralColors.map((ral) => ({
        id: ral.id,
        name: ral.name,
        hex: ral.hex,
        ral: ral.ral,
        number: ral.number,
        category: ral.category,
      })),
    []
  );

  const filteredColors = useMemo(() => {
    let filtered = allColors;

    if (selectedCategory !== 'all') {
      filtered = filtered.filter((color) => color.category === selectedCategory);
    }

    if (searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      filtered = filtered.filter(
        (color) =>
          color.name.toLowerCase().includes(term) ||
          String(color.ral || '').toLowerCase().includes(term) ||
          color.id.toLowerCase().includes(term)
      );
    }

    return filtered;
  }, [allColors, searchTerm, selectedCategory]);

  const selectedColorData = useMemo(() => {
    const direct = allColors.find((c) => c.id === selectedColor);
    if (direct) return direct;
    const resolved = getColorById(selectedColor);
    if (resolved) {
      const byId = allColors.find((c) => c.id === resolved.id);
      if (byId) return byId;
      const byRal = allColors.find((c) => c.ral === resolved.ral);
      if (byRal) return byRal;
    }
    return allColors[0];
  }, [allColors, selectedColor]);

  const closePopover = useCallback(() => {
    setIsOpen(false);
    setSearchTerm('');
    setSelectedCategory('all');
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        closePopover();
      }
    };

    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [isOpen, closePopover]);

  const categoryLabel = (category) => {
    if (category === 'all') return t('options.colorsAll');
    const key = `options.colors${category.charAt(0).toUpperCase()}${category.slice(1)}`;
    const translated = t(key);
    return translated === key
      ? category.charAt(0).toUpperCase() + category.slice(1)
      : translated;
  };

  const handleSelectColor = (colorId) => {
    onColorChange?.(colorId);
    closePopover();
  };

  const selectedLabel = selectedColorData.ral
    ? `RAL ${selectedColorData.ral}`
    : selectedColorData.name;

  const palette = (
    <div className="bg-white">
      {showSearch && (
        <div className="p-2 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
            <input
              type="text"
              placeholder="Search by RAL code or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-8 pr-8 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
            />
            {searchTerm ? (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            ) : null}
          </div>

          <div className="flex flex-wrap gap-1.5 mt-2">
            {colorCategories.map((category) => {
              const active = selectedCategory === category;
              return (
                <button
                  type="button"
                  key={category}
                  aria-pressed={active}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-2 py-1 text-xs rounded-full border transition-all ${
                    active
                      ? 'bg-orange-500 text-white border-orange-500'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-orange-300'
                  }`}
                >
                  {categoryLabel(category)}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div
        className="overflow-y-auto overflow-x-hidden"
        style={{ maxHeight: maxListHeight, minHeight: 100 }}
      >
        {filteredColors.length > 0 ? (
          <div className="p-1">
            {filteredColors.map((color) => {
              const isSelected =
                selectedColor === color.id || selectedColorData?.id === color.id;
              return (
                <button
                  type="button"
                  key={color.id}
                  onClick={() => handleSelectColor(color.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-gray-100 transition-colors ${
                    isSelected ? 'bg-orange-50' : ''
                  }`}
                >
                  <div
                    className="w-8 h-8 rounded border border-gray-300 flex-shrink-0"
                    style={{ backgroundColor: color.hex }}
                  />
                  <div className="flex-1 text-left min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {color.ral ? `RAL ${color.ral}` : color.name}
                    </div>
                    {color.ral && color.name !== `RAL ${color.ral}` ? (
                      <div className="text-xs text-gray-500 truncate">{color.name}</div>
                    ) : null}
                  </div>
                  {isSelected ? (
                    <Check className="w-4 h-4 text-orange-500 flex-shrink-0" />
                  ) : null}
                </button>
              );
            })}
          </div>
        ) : (
          <div className="p-4 text-center text-sm text-gray-500">No colors found</div>
        )}
      </div>

      {(searchTerm || selectedCategory !== 'all') && filteredColors.length > 0 ? (
        <div className="px-3 py-2 text-xs text-gray-500 border-t border-gray-200 bg-gray-50">
          {filteredColors.length} color{filteredColors.length !== 1 ? 's' : ''} found
        </div>
      ) : null}
    </div>
  );

  return (
    <div className={inline ? 'space-y-2' : 'relative'} ref={rootRef}>
      <button
        type="button"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
        className="w-full flex items-center justify-between gap-2 h-9 px-3 py-2 text-sm border border-input bg-transparent rounded-md shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div
            className="w-5 h-5 rounded border border-gray-300 flex-shrink-0"
            style={{ backgroundColor: selectedColorData.hex }}
          />
          <span className="text-left truncate">{selectedLabel}</span>
        </div>
        <ChevronDown
          className={`h-4 w-4 opacity-50 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen ? (
        <div
          className={
            inline
              ? 'border border-gray-200 rounded-md shadow-sm overflow-hidden'
              : 'absolute z-50 left-0 right-0 mt-1 border border-gray-200 rounded-md shadow-lg overflow-hidden bg-white'
          }
        >
          {palette}
        </div>
      ) : null}
    </div>
  );
};

export default ColorPicker;
